import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
    LineChart, Line, PieChart, Pie, Cell, AreaChart, Area
} from 'recharts';
import {
    ArrowRight, Download, Filter, Calendar, TrendingUp, Users,
    DollarSign, CheckSquare, ArrowLeft, X, RefreshCw, ChartColumn, TrendingDown
} from 'lucide-react';

// Import fetch functions
import { getListPaymentsByBuildingId } from '../../Fetch/fetchPayment';
import { getExpenses } from '../../Fetch/fetchExpenses';
import { getTasks } from '../../Fetch/fetchTasks';
import { getListUsers } from '../../Fetch/fetchUser';

// Import CSS
import styles from './Reports.module.css';

const Reports = ({ onNavigateBack }) => {
    const navigate = useNavigate();

    // State for data
    const [paymentsData, setPaymentsData] = useState([]);
    const [expensesData, setExpensesData] = useState([]);
    const [tasksData, setTasksData] = useState([]);
    const [usersData, setUsersData] = useState([]);
    const [buildingData, setBuildingData] = useState(null);

    // State for UI
    const [activeReport, setActiveReport] = useState('revenue'); // revenue, expenses, tasks
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [isLoading, setIsLoading] = useState(false);
    const [dateRange, setDateRange] = useState('year'); // year, 6months, 3months

    // Colors for charts
    const colors = {
        revenue: '#10b981',
        expenses: '#ef4444',
        netProfit: '#6366f1',
        tasks: '#8b5cf6',
        users: '#3b82f6'
    };

    // Load data on component mount
    useEffect(() => {
        loadReportsData();
    }, []);

    const loadReportsData = async () => {
        setIsLoading(true);
        try {
            const storedUser = localStorage.getItem('user');
            if (storedUser) {
                const user = JSON.parse(storedUser);
                const buildingId = user.buildingId;

                if (!buildingId) {
                    return;
                }

                // Load all data in parallel
                const [payments, expenses, tasks, users] = await Promise.all([
                    getListPaymentsByBuildingId(buildingId),
                    getExpenses(buildingId),
                    getTasks(buildingId),
                    getListUsers(buildingId)
                ]);

                setPaymentsData(payments || []);
                setExpensesData(expenses?.data || expenses || []);
                setTasksData(tasks?.tasks || tasks || []);
                setUsersData(users || []);
            }
        } catch (error) {
        } finally {
            setIsLoading(false);
        }
    };

    // Process revenue data by month
    const processRevenueData = () => {
        const monthlyData = {};
        const currentDate = new Date();

        // Initialize all months for selected year
        for (let month = 1; month <= 12; month++) {
            const monthKey = `${selectedYear}-${month.toString().padStart(2, '0')}`;
            monthlyData[monthKey] = {
                month: new Date(selectedYear, month - 1).toLocaleDateString('he-IL', { month: 'short' }),
                revenue: 0,
                transactions: 0,
                users: usersData.length
            };
        }

        // Process payments
        paymentsData.forEach(payment => {
            const paymentDate = new Date(payment.Date || payment.date);
            if (paymentDate.getFullYear() === selectedYear) {
                const monthKey = `${paymentDate.getFullYear()}-${(paymentDate.getMonth() + 1).toString().padStart(2, '0')}`;
                if (monthlyData[monthKey]) {
                    monthlyData[monthKey].revenue += payment.Amount || payment.amount || 0;
                    monthlyData[monthKey].transactions += 1;
                }
            }
        });

        return Object.values(monthlyData);
    };

    // Process expenses data by month
    const processExpensesData = () => {
        const monthlyData = {};

        // Initialize all months for selected year
        for (let month = 1; month <= 12; month++) {
            const monthKey = `${selectedYear}-${month.toString().padStart(2, '0')}`;
            monthlyData[monthKey] = {
                month: new Date(selectedYear, month - 1).toLocaleDateString('he-IL', { month: 'short' }),
                expenses: 0,
                transactions: 0,
                categories: new Set()
            };
        }

        // Process expenses
        expensesData.forEach(expense => {
            const expenseDate = new Date(expense.Date || expense.date);
            if (expenseDate.getFullYear() === selectedYear) {
                const monthKey = `${expenseDate.getFullYear()}-${(expenseDate.getMonth() + 1).toString().padStart(2, '0')}`;
                if (monthlyData[monthKey]) {
                    monthlyData[monthKey].expenses += expense.Amount || expense.amount || 0;
                    monthlyData[monthKey].transactions += 1;
                    if (expense.Category || expense.category) {
                        monthlyData[monthKey].categories.add(expense.Category || expense.category);
                    }
                }
            }
        });

        // Convert categories Set to count
        Object.values(monthlyData).forEach(item => {
            item.categories = item.categories.size;
        });

        return Object.values(monthlyData);
    };

    // Process fixed expenses data by month (הוצאות קבועות)
    const processFixedExpensesData = () => {
        const monthlyData = {};

        // Initialize all months for selected year
        for (let month = 1; month <= 12; month++) {
            const monthKey = `${selectedYear}-${month.toString().padStart(2, '0')}`;
            monthlyData[monthKey] = {
                month: new Date(selectedYear, month - 1).toLocaleDateString('he-IL', { month: 'short' }),
                fixedExpenses: 0,
                transactions: 0,
                categories: new Set()
            };
        }

        // Process only fixed expenses (ExpenseType = "קבועה")
        expensesData.forEach(expense => {
            const expenseDate = new Date(expense.Date || expense.date);
            const expenseType = expense.ExpenseType || expense.expenseType;

            if (expenseDate.getFullYear() === selectedYear && expenseType === 'קבועה') {
                const monthKey = `${expenseDate.getFullYear()}-${(expenseDate.getMonth() + 1).toString().padStart(2, '0')}`;
                if (monthlyData[monthKey]) {
                    monthlyData[monthKey].fixedExpenses += expense.Amount || expense.amount || 0;
                    monthlyData[monthKey].transactions += 1;
                    if (expense.Category || expense.category) {
                        monthlyData[monthKey].categories.add(expense.Category || expense.category);
                    }
                }
            }
        });

        // Convert categories Set to count
        Object.values(monthlyData).forEach(item => {
            item.categories = item.categories.size;
        });

        return Object.values(monthlyData);
    };

    const processVariableExpensesData = () => {
        const monthlyData = {};

        // Initialize all months for selected year
        for (let month = 1; month <= 12; month++) {
            const monthKey = `${selectedYear}-${month.toString().padStart(2, '0')}`;
            monthlyData[monthKey] = {
                month: new Date(selectedYear, month - 1).toLocaleDateString('he-IL', { month: 'short' }),
                variableExpenses: 0,
                transactions: 0,
                categories: new Set()
            };
        }

        // Process only variable expenses (ExpenseType = "משתנה")
        expensesData.forEach(expense => {
            const expenseDate = new Date(expense.Date || expense.date);
            const expenseType = expense.ExpenseType || expense.expenseType;

            if (expenseDate.getFullYear() === selectedYear && expenseType === 'משתנה') {
                const monthKey = `${expenseDate.getFullYear()}-${(expenseDate.getMonth() + 1).toString().padStart(2, '0')}`;
                if (monthlyData[monthKey]) {
                    monthlyData[monthKey].variableExpenses += expense.Amount || expense.amount || 0;
                    monthlyData[monthKey].transactions += 1;
                    if (expense.Category || expense.category) {
                        monthlyData[monthKey].categories.add(expense.Category || expense.category);
                    }
                }
            }
        });

        // Convert categories Set to count
        Object.values(monthlyData).forEach(item => {
            item.categories = item.categories.size;
        });

        return Object.values(monthlyData);
    };

    // Process net profit data by month
    const processNetProfitData = () => {
        const monthlyData = {};

        // Initialize all months for selected year
        for (let month = 1; month <= 12; month++) {
            const monthKey = `${selectedYear}-${month.toString().padStart(2, '0')}`;
            monthlyData[monthKey] = {
                month: new Date(selectedYear, month - 1).toLocaleDateString('he-IL', { month: 'short' }),
                revenue: 0,
                expenses: 0,
                netProfit: 0,
                transactions: 0
            };
        }

        // Process payments (revenue)
        paymentsData.forEach(payment => {
            const paymentDate = new Date(payment.Date || payment.date);
            if (paymentDate.getFullYear() === selectedYear) {
                const monthKey = `${paymentDate.getFullYear()}-${(paymentDate.getMonth() + 1).toString().padStart(2, '0')}`;
                if (monthlyData[monthKey]) {
                    monthlyData[monthKey].revenue += payment.Amount || payment.amount || 0;
                    monthlyData[monthKey].transactions += 1;
                }
            }
        });

        // Process expenses
        expensesData.forEach(expense => {
            const expenseDate = new Date(expense.Date || expense.date);
            if (expenseDate.getFullYear() === selectedYear) {
                const monthKey = `${expenseDate.getFullYear()}-${(expenseDate.getMonth() + 1).toString().padStart(2, '0')}`;
                if (monthlyData[monthKey]) {
                    monthlyData[monthKey].expenses += expense.Amount || expense.amount || 0;
                }
            }
        });

        // Calculate net profit for each month
        Object.values(monthlyData).forEach(item => {
            item.netProfit = item.revenue - item.expenses;
        });

        return Object.values(monthlyData);
    };

    // Process tasks data by month
    const processTasksData = () => {
        const monthlyData = {};

        // Initialize all months for selected year
        for (let month = 1; month <= 12; month++) {
            const monthKey = `${selectedYear}-${month.toString().padStart(2, '0')}`;
            monthlyData[monthKey] = {
                month: new Date(selectedYear, month - 1).toLocaleDateString('he-IL', { month: 'short' }),
                total: 0,
                completed: 0,
                inProgress: 0,
                pending: 0
            };
        }

        // Process tasks
        tasksData.forEach(task => {
            const taskDate = new Date(task.date || task.Date);
            if (taskDate.getFullYear() === selectedYear) {
                const monthKey = `${taskDate.getFullYear()}-${(taskDate.getMonth() + 1).toString().padStart(2, '0')}`;
                if (monthlyData[monthKey]) {
                    monthlyData[monthKey].total += 1;

                    const status = task.status || task.Status;
                    if (status === 'הושלם') {
                        monthlyData[monthKey].completed += 1;
                    } else if (status === 'בטיפול') {
                        monthlyData[monthKey].inProgress += 1;
                    } else {
                        monthlyData[monthKey].pending += 1;
                    }
                }
            }
        });

        return Object.values(monthlyData);
    };

    // Get available years from data
    const getAvailableYears = () => {
        const years = new Set();
        const currentYear = new Date().getFullYear();

        // Add current year and previous years
        years.add(currentYear);
        years.add(currentYear - 1);
        years.add(currentYear - 2);

        // Add years from payments
        paymentsData.forEach(payment => {
            const year = new Date(payment.Date || payment.date).getFullYear();
            if (year >= 2020) years.add(year);
        });

        // Add years from expenses
        expensesData.forEach(expense => {
            const year = new Date(expense.Date || expense.date).getFullYear();
            if (year >= 2020) years.add(year);
        });

        return Array.from(years).sort((a, b) => b - a);
    };

    // Calculate summary statistics
    const getSummaryStats = (data, type) => {
        if (!data || data.length === 0) return { total: 0, average: 0, peak: 0, growth: 0 };

        let total = 0;
        let peak = 0;
        let field;

        // Determine the field name based on type
        switch (type) {
            case 'revenue':
                field = 'revenue';
                break;
            case 'expenses':
                field = 'expenses';
                break;
            case 'fixedExpenses':
                field = 'fixedExpenses';
                break;
            case 'variableExpenses':
                field = 'variableExpenses';
                break;
            case 'netProfit':
                field = 'netProfit';
                break;
            default:
                field = 'total';
        }

        data.forEach(item => {
            const value = item[field] || 0;
            total += value;
            if (value > peak) peak = value;
        });

        const average = data.length > 0 ? total / data.length : 0;

        // Calculate growth (comparing first and last non-zero values)
        const firstValue = data.find(item => item[field] > 0)?.[field] || 0;
        const lastValue = data.slice().reverse().find(item => item[field] > 0)?.[field] || 0;
        const growth = firstValue > 0 ? ((lastValue - firstValue) / firstValue) * 100 : 0;

        return { total, average, peak, growth };
    };


    // Handle navigation
    const handleNavigateBack = () => {
        if (onNavigateBack) {
            onNavigateBack();
        } else if (navigate) {
            navigate('/app/dashboard');
        }
    };

    const revenueData = processRevenueData();
    const expensesDataProcessed = processExpensesData();
    const netProfitData = processNetProfitData();
    const tasksDataProcessed = processTasksData();
    const availableYears = getAvailableYears();

    const revenueStats = getSummaryStats(revenueData, 'revenue');
    const expensesStats = getSummaryStats(expensesDataProcessed, 'expenses');
    const netProfitStats = getSummaryStats(netProfitData, 'netProfit');
    const tasksStats = getSummaryStats(tasksDataProcessed, 'tasks');

    const renderRevenueReport = () => (
        <div className={styles.reportSection}>
            <div className={styles.reportHeader}>
                <div className={styles.reportTitle}>
                    <TrendingDown size={24} className={styles.reportIcon} style={{ color: colors.revenue }} />
                    <h2>דוח הכנסות {selectedYear}</h2>
                </div>
                <div className={styles.summaryStats}>
                    <div className={styles.statCard}>
                        <div className={styles.statValue}>₪{revenueStats.total.toLocaleString()}</div>
                        <div className={styles.statLabel}>סה"כ הכנסות</div>
                    </div>
                    <div className={styles.statCard}>
                        <div className={styles.statValue}>₪{Math.round(revenueStats.average).toLocaleString()}</div>
                        <div className={styles.statLabel}>ממוצע חודשי</div>
                    </div>
                    <div className={styles.statCard}>
                        <div className={styles.statValue}>{usersData.length}</div>
                        <div className={styles.statLabel}>דיירים בבניין</div>
                    </div>
                    <div className={styles.statCard}>
                        <div className={styles.statValue} style={{ color: revenueStats.growth > 0 ? colors.revenue : colors.expenses }}>
                            {revenueStats.growth > 0 ? '+' : ''}{revenueStats.growth.toFixed(1)}%
                        </div>
                        <div className={styles.statLabel}>צמיחה שנתית</div>
                    </div>
                </div>
            </div>

            <div className={styles.chartsContainer}>
                <div className={styles.chartWrapper}>
                    <h3>הכנסות חודשיות</h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <AreaChart data={revenueData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="month" />
                            <YAxis />
                            <Tooltip
                                formatter={(value) => [`₪${value.toLocaleString()}`, 'הכנסות']}
                                labelStyle={{ direction: 'rtl' }}
                            />
                            <Area
                                type="monotone"
                                dataKey="revenue"
                                stroke={colors.revenue}
                                fill={colors.revenue}
                                fillOpacity={0.3}
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>

                <div className={styles.chartWrapper}>
                    <h3>מספר תשלומים חודשיים</h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={revenueData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="month" />
                            <YAxis />
                            <Tooltip
                                formatter={(value) => [value, 'תשלומים']}
                                labelStyle={{ direction: 'rtl' }}
                            />
                            <Bar dataKey="transactions" fill={colors.revenue} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </div>
    );


    // Process fixed expenses by supplier
    const processFixedExpensesBySupplier = () => {
        const supplierData = {};

        // Process only fixed expenses (ExpenseType = "קבועה")
        expensesData.forEach(expense => {
            const expenseDate = new Date(expense.Date || expense.date);
            const expenseType = expense.ExpenseType || expense.expenseType;

            if (expenseDate.getFullYear() === selectedYear && expenseType === 'קבועה') {
                // Get supplier name from PaidTo or Vendor field
                const supplier = expense.PaidTo || expense.paidTo || expense.Vendor || expense.vendor || 'לא צוין';

                if (!supplierData[supplier]) {
                    supplierData[supplier] = {
                        name: supplier,
                        total: 0,
                        count: 0,
                        monthlyAverage: 0
                    };
                }

                supplierData[supplier].total += expense.Amount || expense.amount || 0;
                supplierData[supplier].count += 1;
            }
        });

        // Calculate monthly average for each supplier
        Object.values(supplierData).forEach(supplier => {
            supplier.monthlyAverage = supplier.count > 0 ? supplier.total / supplier.count : 0;
        });

        // Convert to array and sort by total (descending)
        return Object.values(supplierData).sort((a, b) => b.total - a.total);
    };

    const renderExpensesReport = () => {
        const fixedExpensesData = processFixedExpensesData();
        const variableExpensesData = processVariableExpensesData();
        const fixedExpensesBySupplier = processFixedExpensesBySupplier();

        // Calculate stats for fixed and variable expenses
        const fixedExpensesStats = getSummaryStats(fixedExpensesData, 'fixedExpenses');
        const variableExpensesStats = getSummaryStats(variableExpensesData, 'variableExpenses');

        return (
            <div className={styles.reportSection}>
                <div className={styles.reportHeader}>
                    <div className={styles.reportTitle}>
                        <TrendingUp size={24} className={styles.reportIcon} style={{ color: colors.expenses }} />
                        <h2>דוח הוצאות {selectedYear}</h2>
                    </div>
                    <div className={styles.summaryStats}>
                        <div className={styles.statCard}>
                            <div className={styles.statValue}>₪{expensesStats.total.toLocaleString()}</div>
                            <div className={styles.statLabel}>סה"כ הוצאות</div>
                        </div>
                        <div className={styles.statCard}>
                            <div className={styles.statValue}>₪{fixedExpensesStats.total.toLocaleString()}</div>
                            <div className={styles.statLabel}>הוצאות קבועות</div>
                        </div>
                        <div className={styles.statCard}>
                            <div className={styles.statValue}>₪{variableExpensesStats.total.toLocaleString()}</div>
                            <div className={styles.statLabel}>הוצאות משתנות</div>
                        </div>
                        <div className={styles.statCard}>
                            <div className={styles.statValue}>{expensesDataProcessed.reduce((sum, item) => sum + item.transactions, 0)}</div>
                            <div className={styles.statLabel}>סה"כ הוצאות</div>
                        </div>
                    </div>
                </div>

                <div className={styles.chartsContainer}>
                    <div className={styles.chartWrapper}>
                        <h3>הוצאות חודשיות</h3>
                        <ResponsiveContainer width="100%" height={300}>
                            <AreaChart data={expensesDataProcessed}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="month" />
                                <YAxis />
                                <Tooltip
                                    formatter={(value) => [`₪${value.toLocaleString()}`, 'הוצאות']}
                                    labelStyle={{ direction: 'rtl' }}
                                />
                                <Area
                                    type="monotone"
                                    dataKey="expenses"
                                    stroke={colors.expenses}
                                    fill={colors.expenses}
                                    fillOpacity={0.3}
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>

                    <div className={styles.chartWrapper}>
                        <h3>הוצאות קבועות חודשיות</h3>
                        <ResponsiveContainer width="100%" height={300}>
                            <AreaChart data={fixedExpensesData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="month" />
                                <YAxis />
                                <Tooltip
                                    formatter={(value) => [`₪${value.toLocaleString()}`, 'הוצאות קבועות']}
                                    labelStyle={{ direction: 'rtl' }}
                                />
                                <Area
                                    type="monotone"
                                    dataKey="fixedExpenses"
                                    stroke="#10b981"
                                    fill="#10b981"
                                    fillOpacity={0.3}
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>

                    <div className={styles.chartWrapper}>
                        <h3>הוצאות משתנות חודשיות</h3>
                        <ResponsiveContainer width="100%" height={300}>
                            <AreaChart data={variableExpensesData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="month" />
                                <YAxis />
                                <Tooltip
                                    formatter={(value) => [`₪${value.toLocaleString()}`, 'הוצאות משתנות']}
                                    labelStyle={{ direction: 'rtl' }}
                                />
                                <Area
                                    type="monotone"
                                    dataKey="variableExpenses"
                                    stroke="#f59e0b"
                                    fill="#f59e0b"
                                    fillOpacity={0.3}
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>

                    <div className={styles.chartWrapper}>
                        <h3>השוואת הוצאות קבועות ומשתנות</h3>
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={expensesDataProcessed.map((item, index) => ({
                                ...item,
                                fixedExpenses: fixedExpensesData[index]?.fixedExpenses || 0,
                                variableExpenses: variableExpensesData[index]?.variableExpenses || 0
                            }))}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="month" />
                                <YAxis />
                                <Tooltip
                                    formatter={(value, name) => [
                                        `₪${value.toLocaleString()}`,
                                        name === 'fixedExpenses' ? 'הוצאות קבועות' : name === 'variableExpenses' ? 'הוצאות משתנות' : name
                                    ]}
                                    labelStyle={{ direction: 'rtl' }}
                                />
                                <Legend />
                                <Bar dataKey="fixedExpenses" fill="#10b981" name="הוצאות קבועות" />
                                <Bar dataKey="variableExpenses" fill="#f59e0b" name="הוצאות משתנות" />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>

                    {/* New Section: Fixed Expenses by Supplier */}
                    <div className={styles.chartWrapper} style={{ gridColumn: '1 / -1' }}>
                        <h3>הוצאות קבועות חודשיות לפי ספק</h3>
                        {fixedExpensesBySupplier.length > 0 ? (
                            <div className={styles.supplierExpensesContainer}>
                                <div className={styles.supplierExpensesTable}>
                                    <div className={styles.supplierExpensesHeader}>
                                        <div className={styles.supplierNameColumn}>ספק</div>
                                        <div className={styles.supplierAmountColumn}>סה"כ שנתי</div>
                                        <div className={styles.supplierAmountColumn}>ממוצע חודשי</div>
                                        <div className={styles.supplierCountColumn}>מספר תשלומים</div>
                                    </div>
                                    {fixedExpensesBySupplier.map((supplier, index) => (
                                        <div key={index} className={styles.supplierExpensesRow}>
                                            <div className={styles.supplierNameColumn}>{supplier.name}</div>
                                            <div className={styles.supplierAmountColumn}>
                                                ₪{supplier.total.toLocaleString()}
                                            </div>
                                            <div className={styles.supplierAmountColumn}>
                                                ₪{Math.round(supplier.monthlyAverage).toLocaleString()}
                                            </div>
                                            <div className={styles.supplierCountColumn}>{supplier.count}</div>
                                        </div>
                                    ))}
                                </div>
                                <div className={styles.supplierExpensesChart}>
                                    <h4>ספקים מובילים</h4>
                                    <div style={{ direction: 'ltr' }}>
                                        <ResponsiveContainer width="100%" height={400}>
                                            <BarChart
                                                data={fixedExpensesBySupplier.slice(0, 10)}
                                                layout="vertical"
                                                margin={{ top: 10, right: 20, left: 160, bottom: 10 }}
                                            >
                                                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                                <XAxis
                                                    type="number"
                                                    tickFormatter={(value) => `₪${value.toLocaleString()}`}
                                                />
                                                <YAxis
                                                    dataKey="name"
                                                    type="category"
                                                    width={150}
                                                    style={{ fontSize: '13px', fontWeight: '500' }}
                                                />
                                                <Tooltip
                                                    formatter={(value) => [`₪${value.toLocaleString()}`, 'סכום שנתי']}
                                                    labelStyle={{ direction: 'rtl', fontWeight: 600 }}
                                                    contentStyle={{
                                                        background: 'rgba(255, 255, 255, 0.98)',
                                                        border: '2px solid #10b981',
                                                        borderRadius: '8px',
                                                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                                                    }}
                                                />
                                                <Bar
                                                    dataKey="total"
                                                    fill="#10b981"
                                                    radius={[0, 8, 8, 0]}
                                                />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className={styles.noDataMessage}>
                                <p>לא נמצאו הוצאות קבועות עבור שנה זו</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const renderNetProfitReport = () => (
        <div className={styles.reportSection}>
            <div className={styles.reportHeader}>
                <div className={styles.reportTitle}>
                    <ChartColumn size={20} className={styles.reportIcon} style={{ color: colors.netProfit }} />
                    <h2>יתרה {selectedYear}</h2>
                </div>
                <div className={styles.summaryStats}>
                    <div className={styles.statCard}>
                        <div className={styles.statValue}>₪{netProfitStats.total.toLocaleString()}</div>
                        <div className={styles.statLabel}>סה"כ יתרה</div>
                    </div>
                    <div className={styles.statCard}>
                        <div className={styles.statValue}>₪{Math.round(netProfitStats.average).toLocaleString()}</div>
                        <div className={styles.statLabel}>ממוצע חודשי</div>
                    </div>
                    <div className={styles.statCard}>
                        <div className={styles.statValue}>₪{revenueStats.total.toLocaleString()}</div>
                        <div className={styles.statLabel}>סה"כ הכנסות</div>
                    </div>
                    <div className={styles.statCard}>
                        <div className={styles.statValue}>₪{expensesStats.total.toLocaleString()}</div>
                        <div className={styles.statLabel}>סה"כ הוצאות</div>
                    </div>
                    <div className={styles.statCard}>
                        <div className={styles.statValue} style={{ color: netProfitStats.growth > 0 ? colors.revenue : colors.expenses }}>
                            {netProfitStats.growth > 0 ? '+' : ''}{netProfitStats.growth.toFixed(1)}%
                        </div>
                        <div className={styles.statLabel}>צמיחה שנתית</div>
                    </div>
                </div>
            </div>

            <div className={styles.chartsContainer}>
                <div className={styles.chartWrapper}>
                    <h3>הכנסות מול הוצאות</h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={netProfitData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="month" />
                            <YAxis />
                            <Tooltip
                                formatter={(value, name) => [
                                    `₪${value.toLocaleString()}`,
                                    name === 'revenue' ? 'הכנסות' : name === 'expenses' ? 'הוצאות' : 'יתרה'
                                ]}
                                labelStyle={{ direction: 'rtl' }}
                            />
                            <Legend />
                            <Bar dataKey="revenue" fill={colors.revenue} name="הכנסות" />
                            <Bar dataKey="expenses" fill={colors.expenses} name="הוצאות" />
                        </BarChart>
                    </ResponsiveContainer>
                </div>

                <div className={styles.chartWrapper}>
                    <h3>מגמת יתרה</h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={netProfitData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="month" />
                            <YAxis />
                            <Tooltip
                                formatter={(value) => [`₪${value.toLocaleString()}`, 'יתרה']}
                                labelStyle={{ direction: 'rtl' }}
                            />
                            <Line
                                type="monotone"
                                dataKey="netProfit"
                                stroke={colors.netProfit}
                                strokeWidth={3}
                                dot={{ fill: colors.netProfit, r: 6 }}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </div>

                <div className={styles.chartWrapper}>
                    <h3>יתרה חודשי</h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <AreaChart data={netProfitData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="month" />
                            <YAxis />
                            <Tooltip
                                formatter={(value) => [`₪${value.toLocaleString()}`, 'יתרה']}
                                labelStyle={{ direction: 'rtl' }}
                            />
                            <Area
                                type="monotone"
                                dataKey="netProfit"
                                stroke={colors.netProfit}
                                fill={colors.netProfit}
                                fillOpacity={0.3}
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </div>
    );


    const renderTasksReport = () => (
        <div className={styles.reportSection}>
            <div className={styles.reportHeader}>
                <div className={styles.reportTitle}>
                    <CheckSquare size={24} className={styles.reportIcon} style={{ color: colors.tasks }} />
                    <h2>דוח משימות {selectedYear}</h2>
                </div>
                <div className={styles.summaryStats}>
                    <div className={styles.statCard}>
                        <div className={styles.statValue}>{tasksStats.total}</div>
                        <div className={styles.statLabel}>סה"כ משימות</div>
                    </div>
                    <div className={styles.statCard}>
                        <div className={styles.statValue}>{tasksDataProcessed.reduce((sum, item) => sum + item.completed, 0)}</div>
                        <div className={styles.statLabel}>משימות הושלמו</div>
                    </div>
                    <div className={styles.statCard}>
                        <div className={styles.statValue}>{tasksDataProcessed.reduce((sum, item) => sum + item.inProgress, 0)}</div>
                        <div className={styles.statLabel}>בטיפול</div>
                    </div>
                    <div className={styles.statCard}>
                        <div className={styles.statValue}>{tasksDataProcessed.reduce((sum, item) => sum + item.pending, 0)}</div>
                        <div className={styles.statLabel}>ממתינות</div>
                    </div>
                </div>
            </div>

            <div className={styles.chartsContainer}>
                <div className={styles.chartWrapper}>
                    <h3>משימות לפי חודש וסטטוס</h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={tasksDataProcessed}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="month" />
                            <YAxis />
                            <Tooltip labelStyle={{ direction: 'rtl' }} />
                            <Legend />
                            <Bar dataKey="completed" stackId="a" fill="#10b981" name="הושלמו" />
                            <Bar dataKey="inProgress" stackId="a" fill="#f59e0b" name="בטיפול" />
                            <Bar dataKey="pending" stackId="a" fill="#ef4444" name="ממתינות" />
                        </BarChart>
                    </ResponsiveContainer>
                </div>

                <div className={styles.chartWrapper}>
                    <h3>סה"כ משימות חודשיות</h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={tasksDataProcessed}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="month" />
                            <YAxis />
                            <Tooltip
                                formatter={(value) => [value, 'משימות']}
                                labelStyle={{ direction: 'rtl' }}
                            />
                            <Line
                                type="monotone"
                                dataKey="total"
                                stroke={colors.tasks}
                                strokeWidth={3}
                                dot={{ fill: colors.tasks, r: 6 }}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </div>
    );

    return (
        <div className={styles.reportsContainer}>
            {/* Header */}
            <div className={styles.reportsHeader}>
                <div className={styles.controlsSection}>
                    <div className={styles.yearSelector}>
                        <Calendar size={16} />
                        <select
                            value={selectedYear}
                            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                            className={styles.yearSelect}
                        >
                            {availableYears.map(year => (
                                <option key={year} value={year}>{year}</option>
                            ))}
                        </select>
                    </div>

                    <div className={styles.reportTabs}>
                        <button
                            className={`${styles.tabButton} ${activeReport === 'revenue' ? styles.active : ''}`}
                            onClick={() => setActiveReport('revenue')}
                        >
                            <TrendingDown size={20} />
                            הכנסות
                        </button>
                        <button
                            className={`${styles.tabButton} ${activeReport === 'expenses' ? styles.active : ''}`}
                            onClick={() => setActiveReport('expenses')}
                        >
                            <TrendingUp size={18} />
                            הוצאות
                        </button>
                        <button
                            className={`${styles.tabButton} ${activeReport === 'netProfit' ? styles.active : ''}`}
                            onClick={() => setActiveReport('netProfit')}
                        >
                            <ChartColumn size={20} />
                            יתרה
                        </button>
                        <button
                            className={`${styles.tabButton} ${activeReport === 'tasks' ? styles.active : ''}`}
                            onClick={() => setActiveReport('tasks')}
                        >
                            <CheckSquare size={18} />
                            משימות
                        </button>
                    </div>
                </div>
            </div>

            {/* Loading State */}
            {isLoading && (
                <div className={styles.loadingContainer}>
                    <RefreshCw size={24} className={styles.rotating} />
                    <p>טוען נתוני דוחות...</p>
                </div>
            )}

            {/* Report Content */}
            {!isLoading && (
                <div className={styles.reportsContent}>
                    {activeReport === 'revenue' && renderRevenueReport()}
                    {activeReport === 'expenses' && renderExpensesReport()}
                    {activeReport === 'netProfit' && renderNetProfitReport()}
                    {activeReport === 'tasks' && renderTasksReport()}
                </div>
            )}
        </div>
    );
};

export default Reports;